// Canonical, vendor-neutral vocabulary for the PMS (property management system)
// integration layer. See docs/Integrations/00_pms_adapter_plan.md.
//
// Everything in core/ is PURE: no aws-amplify, no fetch, no DOM. The never-send
// guard that lives here is a safety control, so it has to be unit-testable and
// reusable from a Lambda later without a second copy that can rot.
//
// Conventions: money is always integer cents, dates are always ISO strings, and
// ids are always OUR ids unless the field name says `external`.

import type { PmsError } from './errors';

export type PmsProvider = 'ssm' | 'sitelink' | 'mock' | 'none';

export type PmsOp = 'ensureTenant' | 'moveIn' | 'recordPayment' | 'moveOut' | 'getUnit';

export type PmsEntity = 'tenant' | 'unit' | 'lease' | 'payment';

/** 'shadow' builds and records the request but never sends it. */
export type PmsMode = 'shadow' | 'live';

/**
 * Placeholder for a vendor id we do not know yet. moveIn/recordPayment need
 * SSM's TenantId/UnitId, which only exist once ensureTenant has run for real
 * (phase 2) and units have been backfilled (phase 6). Until then the built body
 * carries this sentinel and the shadow record enumerates which fields hold it.
 */
export const UNRESOLVED = '__UNRESOLVED__';

/**
 * The only two payment methods we may ever send. 'creditcard' / 'ACH' / 'EFT'
 * would run the charge through the FACILITY's gateway, double-charging a
 * customer Stripe has already billed.
 */
export type SsmPaymentMethod = 'cash' | 'check';

export interface PmsAddress {
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  zip: string;
}

// ---------------------------------------------------------------------------
// Operation inputs / outputs
// ---------------------------------------------------------------------------

export interface EnsureTenantInput {
  binsTenantID: string; // our Tenant.id (an email)
  name: string;
  email: string;
  phone: string;
  address: PmsAddress;
  /** Present when we already know SSM's tenant id, which turns this into an update. */
  externalTenantID?: string | null;
  accessCode?: string | null;
}

export interface EnsureTenantOutput {
  externalTenantID: string;
  accessCode?: string | null;
}

export interface MoveInInput {
  binsTenantID: string;
  binsUnitID: string;
  externalTenantID?: string | null;
  externalUnitID?: string | null;
  startDateISO: string;
  /** The rent WE charge, from the pricing system. Overrides SSM's board rate. */
  rentAmountCents: number;
  /** What we actually collected at move-in; 0 is allowed. */
  amountCents: number;
  tenantName?: string | null;
  accessCode?: string | null;
}

export interface MoveInOutput {
  externalLeaseID: string;
  receiptNumber?: string | null;
  accessCode?: string | null;
}

export interface RecordPaymentInput {
  binsTenantID: string;
  binsLedgerEntryID: string;
  externalTenantID?: string | null;
  externalUnitID?: string | null;
  tenantName?: string | null;
  amountCents: number;
  paidAtISO: string;
  /** Stripe payment intent id, or "zelle:<date>" — lands in SSM as the check number. */
  reference: string;
  /** True when the ledger row combines rent with our own fees (see plan §5). */
  mixedCharge?: boolean;
}

export interface RecordPaymentOutput {
  receiptNumber: string;
}

export interface MoveOutInput {
  externalTenantID: string;
  externalUnitID: string;
  moveOutDateISO: string;
}

export interface MoveOutOutput {
  closedOnISO: string;
}

export interface GetUnitInput {
  externalUnitID: string;
}

export interface GetUnitOutput {
  vacant: boolean;
  externalLeaseID?: string | null;
}

// ---------------------------------------------------------------------------
// Transport envelope + results
// ---------------------------------------------------------------------------

/** Exactly what we would put on the wire. The unit of review in shadow mode. */
export interface PmsRequestEnvelope {
  op: PmsOp;
  vendorOp: string; // 'AddUpdateAccount' | 'ProcessMoveIn' | 'ProcessPayment' | ...
  path: string;
  body: unknown;
}

/**
 * Operations return a result rather than throwing, and carry the built request
 * on BOTH paths — shadow mode and the guard test need the request even when the
 * call succeeded. (This is a deliberate deviation from §6 of the design doc.)
 */
export type PmsOpResult<T> =
  | { ok: true; data: T; request: PmsRequestEnvelope }
  | { ok: false; error: PmsError; request: PmsRequestEnvelope };

export interface PmsLogger {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
}

// ---------------------------------------------------------------------------
// Per-facility configuration (non-secret only — this ships in the browser bundle)
// ---------------------------------------------------------------------------

export interface SsmFacilityConfig {
  /** SSM's own numeric facility id. */
  facilityId: number;
  /** SSM country id for addresses. US = 1. */
  countryId: number;
  paymentMethod: SsmPaymentMethod;
  /** Shows in SSM so staff can see where the money actually came from. */
  checkBankName: string;
  adSource: string;
  mediumDesc: string;
  /** 151 Internet, 152 Internet - Mobile. */
  inquirySource: number;
  /**
   * null = decline coverage, which is what SSM's docs say to pass when no
   * scheme is selected. NOTE: @ssm/client currently types InsuranceId as a
   * required non-nullable number, so it would reject null before the network
   * call — Bobby needs to relax that schema (see plan notes).
   */
  insuranceId: number | null;
  /** Valet fees are OUR revenue and would exceed SSM's amount due. Always false. */
  postFeesToSsm: false;
  dailyTransactionBudget: number;
}

export interface PmsFacilityConfig {
  facilityID: string;
  provider: PmsProvider;
  mode: PmsMode;
  /** A NAME, never a credential. Resolved server-side in a later phase. */
  secretRef?: string;
  ssm?: SsmFacilityConfig;
}

export interface PmsCtx {
  facilityID: string;
  provider: PmsProvider;
  config: PmsFacilityConfig;
  idempotencyKey: string;
  mode: PmsMode;
  logger: PmsLogger;
  /** Injected so core stays deterministic under test. */
  now: () => string;
}

// ---------------------------------------------------------------------------
// The adapter contract
// ---------------------------------------------------------------------------

export interface PmsAdapter {
  provider: PmsProvider;
  /**
   * What this vendor can actually do. Core code checks the capability, never
   * the provider name — SSM has no move-out endpoint, so it omits 'moveOut'
   * and callers fall back to an admin task.
   */
  capabilities: ReadonlySet<PmsOp>;

  ensureTenant(input: EnsureTenantInput, ctx: PmsCtx): Promise<PmsOpResult<EnsureTenantOutput>>;
  moveIn(input: MoveInInput, ctx: PmsCtx): Promise<PmsOpResult<MoveInOutput>>;
  recordPayment(input: RecordPaymentInput, ctx: PmsCtx): Promise<PmsOpResult<RecordPaymentOutput>>;
  moveOut?(input: MoveOutInput, ctx: PmsCtx): Promise<PmsOpResult<MoveOutOutput>>;
  getUnit?(input: GetUnitInput, ctx: PmsCtx): Promise<PmsOpResult<GetUnitOutput>>;
}
